import { z } from "zod";

import { type CGLSubmission } from "@ca/types/commercial_general_liability";
import { InsuredContactShape } from "@ca/types/insured_contact";
import { type ProductsLiabilitySubmission } from "@ca/types/products_liability";
import { type UASSubmission } from "@ca/types/unmanned_aircraft_systems";

import { FaaAirmanRatingSchema } from "./airmen";
export const useTypes = z.enum([
	"industrial_aid",
	"pleasure_and_business",
	"agricultural",
	"commercial_no_charter",
	"commercial_with_charter",
	"government",
	"flying_clubs",
	"any_use_as_required_by_named_insured",
	"any_use_as_required_by_named_insured_excluding_for_hire_or_reward",
	"contingent_coverage",
]);

const storageTypes = z.enum([
	"hangar",
	"outdoor_tie_down",
	"covered_outdoor_tie_down",
]);

export const applicantType = z.enum([
	"company",
	"individual",
	"partnership",
	"other",
]);

const aircraftTypes = z.enum([
	"fixed_wing",
	"rotor_wing",
]);

export const aircraft = z.object({
	homeAirport: z.string().nullable().describe("the airport code of the home airport of the aircraft"),
	hullValue: z.number().describe("the hull value of the aircraft. This is the value of the aircraft if it were to be sold on the open market. This is not the purchase price."),
	skiFloatValue: z.number().nullable().default(null).describe("the value of skis and floats for the aircraft. Set to null if not provided."),
	liabilityLimit: z.number().describe("the liability limit of the aircraft. This is the amount of money that the aircraft is insured for."),
	liabilitySublimit: z.number().nullable().default(null).describe("typically per person. Should be null if not provided as many insureds prefer no sublimit. Sometimes represented like so `1/100k` which would be a million dollar liability limit with a $100k sublimit."),
	medicalPayments: z.number().nullable().default(null),
	medicalPaymentsLimitText: z.string().nullable().default(null).describe("the basis or coverage class for the medical payments coverage. For example 'per person' or 'per accident', or 'per person including crew"),
	make: z.string(),
	model: z.string(),
	seats: z.string()
		.describe("the number of seats in the aircraft. Often notated as 1+4 for example, or 2+15. Do not add these numbers"),
	tailNumber: z.string().describe("the tail number or registration number of the aircraft"),
	year: z.string().nullable().describe("the 4 digit year of the aircraft. Set to null if unknown"),
	icaoCode: z.string().nullable().describe("the ICAO code of the aircraft. This is a 4 letter code that is used to identify the aircraft. It is not the same as the tail number."),
	mainUse: useTypes.optional().nullable().describe("The primary use of the aircraft"),
	storageType: storageTypes.optional().nullable().describe("The type of storage for the aircraft"),
	aircraftType: aircraftTypes.optional().nullable().describe("Fixed wing or rotor wing aircraft"),
});

const faaVerifiedCertificate = z.object({
	type: z.string().nullable().default(null).describe("FAA certificate type code (e.g. P, F, A)"),
	level: z.string().nullable().default(null).describe("FAA certificate level code"),
	ratings: z.array(FaaAirmanRatingSchema).default([]).describe("pilot ratings on the certificate (e.g. ASEL, AMEL)"),
	typeRatings: z.array(FaaAirmanRatingSchema).default([]).describe("aircraft type ratings (e.g. B-737, DC-9)"),
});

// A structured, verified record of a pilot's FAA airmen registry match. Populated after parsing by a
// dedicated FAA lookup (see researchFaaAirmen); its presence means a confident match was found — when
// no confident match exists the whole faaMatch field is null, not an object.
export const pilotFaaMatch = z.object({
	pilotName: z.string().describe("the submission pilot name this match corresponds to"),
	uniqueId: z.string().describe("FAA Unique ID of the matched airman"),
	registryName: z.string().nullable().default(null).describe("the matched airman's name as it appears in the registry"),
	medicalClass: z.string().nullable().default(null).describe("verified medical class, or null"),
	certificates: z.array(faaVerifiedCertificate).default([]).describe("verified certificates from the matched record"),
	claimedButMissing: z.array(z.string()).default([]).describe("certifications claimed in the submission but absent from the matched record"),
	note: z.string().nullable().default(null).describe("short note on the match"),
	dataUpdatedAt: z.string().nullable().default(null).describe("ISO timestamp the matched FAA record was last refreshed (the monthly ingest run), for showing data freshness"),
});

// Extraction (model-facing) shape omits faaMatch — exposing it risks a hallucinated match aborting the parse.
export const extractedPilot = z.object({
	dob: z.string().nullable()
		.describe("pilot date of birth formatted as YYYY-MM-DD. Set to null if unknown"),
	age: z.number().nullable().optional().default(null)
		.describe("the age of the pilot. Set to null if unknown"),
	hours: z.number().nullable()
		.describe("total flight hours accumlated by the pilot. Set to null if unknown"),
	timeInMakeModel: z.number().nullable().optional().default(null)
		.describe("total flight hours that the pilot has with the make/model aircraft being insured. Set to null if unknown"),
	name: z.string().describe("the full name of the pilot"),
	qualifications: z.array(z.string()).default([])
		.describe("A list of qualifications that the pilot has. This is a list of strings, and each string is a qualification. If the pilot has no qualifications, set to an empty array."),
});

export const pilot = extractedPilot.extend({
	faaMatch: pilotFaaMatch.nullable().optional()
		.describe("verified FAA registry match written by a separate lookup after extraction — an object when a confident match is found, null/absent when none"),
});


export const GASubmissionSchema = z.object({
	...InsuredContactShape,
	lob: z.literal("general_aviation"),
	address: z.object({
		city: z.string(),
		state: z.string().describe("State of the insured. Should be 2 letters, like TX for Texas"),
		streetAddress: z.string(),
		zip: z.string(),
	}).describe("Address of the insured"),
	aircraft: z.array(aircraft).describe("a list of all aircraft that should be insured"),
	brokerage: z.string().describe("the name of the brokerage submitting the submission"),
	brokerName: z.string().describe("the name of the broker submitting the submission").nullable().default(null),
	brokerEmail: z.string().describe("the email of the broker submitting the submission. Sometimes submissions are forwarded from classa.ai emails. classa.ai emails are never the broker.").nullable().default(null),
	inceptionDate: z.iso.date().describe("the date in which the policy would start. For an existing policy, this would be the existing expiration date. For a new purchase, this would be the closing date of the purchase.").nullable().default(null),
	nameOfInsured: z.string().describe("the name of the insured. Might be a company or an individual"),
	newPurchase: z.boolean().nullable()
		.describe("whether the submission is for the purchase of a new aircraft. This information will not always be provided, so set to null if unknown"),
	pilots: z.array(pilot),
	aircraftMaintenanceProvider: z.string().optional().nullable()
		.describe("the name of the aircraft maintenance provider. This is the company that is responsible for the maintenance of the aircraft. Leave null if unknown"),
	applicantType: applicantType
		.nullable()
		.optional()
		.default(null)
		.describe("the type of applicant. This is the type of entity that is applying for insurance. Leave null if unknown"),
	hasIncidentHistory: z.boolean()
		.optional()
		.nullable()
		.default(null)
		.describe("true if there are any reported claims, accidents, violations, or license suspensions."),
	isOwnerFlown: z.boolean().optional().describe("true if any of the aircraft in the submission are flown by the owner/owners. false otherwise"),
});

export const incidentSchema = z.object({
	description: z.string().describe("A description of the incident that occurred"),
	date: z.string().describe("The date of the incident. Format as YYYY-MM-DD or YYYY-MM or YYYY. Exact date is preferred, but year/month or year only is okay.").nullable().default(null),
	aircraft: z.string().describe("The aircraft that was involved in the incident").nullable().default(null),
});
export const submissionIncidentsSchema = z.array(incidentSchema);

export const GASubmissionExtractionSchema = GASubmissionSchema.extend({
	pilots: z.array(extractedPilot),
});

export type GASubmission = z.output<typeof GASubmissionSchema>;
export type SubmissionAircraft = z.output<typeof aircraft>;
export type SubmissionAircraftType = z.output<typeof aircraftTypes>;
export type SubmissionApplicantType = z.output<typeof applicantType>;
export type SubmissionIncident = z.output<typeof incidentSchema>;
export type SubmissionIncidents = z.output<typeof submissionIncidentsSchema>;
export type SubmissionPilot = z.output<typeof pilot>;
export type PilotFaaMatch = z.output<typeof pilotFaaMatch>;
export type SubmissionStorageType = z.output<typeof storageTypes>;
export type SubmissionUseType = z.output<typeof useTypes>;

export type ExtractedSubmission = GASubmission | ProductsLiabilitySubmission | CGLSubmission | UASSubmission;
